import { watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { canAccessWeeklyCheckInModuleFromStore, isWeeklyCheckInModuleNavigation } from '@/utils/weeklyCheckInRouteAccess'

/**
 * Composable that watches for permission changes and redirects users
 * if they lose access to the current page.
 * 
 * Usage: Call this in App.vue or a root component
 */
export function usePermissionGuard() {
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()

  /**
   * Check if user has access to current route
   */
  const checkCurrentRouteAccess = () => {
    if (!authStore.user || !authStore.isAuthenticated) {
      return
    }

    if (authStore.impersonationTransitionInProgress) {
      return
    }

    // Check explicit permission requirement (permissions-only authorization)
    // Authenticated routes need requiredPermission(s), meta.personal, meta.public, or meta.apiAuthorizes
    // Super admin bypass is handled by hasPermission() checking super.admin
    // Use route.matched to find the route record with permission metadata (same as router guard)
    const routeWithPermission = route.matched.find((record: any) => 
      record.meta?.requiredPermission || record.meta?.requiredPermissions
    )
    const requiredPermission = routeWithPermission?.meta?.requiredPermission as string | undefined
    const requiredPermissions = routeWithPermission?.meta?.requiredPermissions as string[] | undefined

    if (requiredPermission) {
      // Route has single permission requirement
      if (!authStore.hasPermission(requiredPermission)) {
        // Fail-safe: deny access if permission missing (see Section 0.2: Fail-Safe Rule)
        console.error(`Route ${route.path} requires permission ${requiredPermission} but user does not have it. Access denied.`)
        router.push('/home')
        return
      }
    } else if (requiredPermissions && Array.isArray(requiredPermissions) && requiredPermissions.length > 0) {
      // Route has multiple permission requirements (user needs at least one)
      if (!authStore.hasAnyPermission(requiredPermissions)) {
        // Fail-safe: deny access if user lacks all required permissions (see Section 0.2: Fail-Safe Rule)
        console.error(`Route ${route.path} requires one of permissions ${requiredPermissions.join(', ')} but user does not have any. Access denied.`)
        router.push('/home')
        return
      }
    } else if (isWeeklyCheckInModuleNavigation(route)) {
      if (!canAccessWeeklyCheckInModuleFromStore(authStore)) {
        console.error(`Route ${route.path} requires Weekly Check-In module access. Access denied.`)
        router.push('/home')
        return
      }
      // Coordinator / print / dashboard access: enforced by each view + API (403). Do not redirect here:
      // case-sensitive uuid compare and super-admin / extended-coordinator flows caused false /home redirects
      // and contradicted WeeklyCheckInCoordinatorView accessOk.
      return
    } else if (route.matched.some((record: any) => record.meta.requiresAuth === true)) {
      const isPublic = route.matched.some((record: any) => record.meta.public === true)
      const isPersonal = route.matched.some((record: any) => record.meta.personal === true)
      const apiAuthorizes = route.matched.some((record: any) => record.meta.apiAuthorizes === true)

      if (!isPublic && !isPersonal && !apiAuthorizes) {
        console.error(
          `Route ${route.path} requires authentication but has no requiredPermission, public, personal, or apiAuthorizes flag. Access denied.`
        )
        router.push('/home')
        return
      }
    }
  }

  // Watch for user permission changes
  let unwatchUser: (() => void) | null = null
  let unwatchRoute: (() => void) | null = null
  let unwatchImpersonationTransition: (() => void) | null = null
  let visibilityChangeHandler: ((event: Event) => void) | null = null
  let hiddenTimestamp: number | null = null
  let permissionCheckInterval: number | null = null

  onMounted(() => {
    // Watch user changes (permissions/roles might have changed)
    unwatchUser = watch(
      () => authStore.user,
      (newUser, oldUser) => {
        // Only check if user actually changed (not just reference)
        if (newUser && oldUser) {
          const newPerms = JSON.stringify(newUser.permissions || [])
          const oldPerms = JSON.stringify(oldUser.permissions || [])
          const newRoles = JSON.stringify(newUser.roles || [])
          const oldRoles = JSON.stringify(oldUser.roles || [])

          if (newPerms !== oldPerms || newRoles !== oldRoles) {
            // Permissions or roles changed - check access
            checkCurrentRouteAccess()
          }
        } else if (newUser && !oldUser) {
          // User logged in - check access
          checkCurrentRouteAccess()
        }
      },
      { deep: true }
    )

    // Watch route changes
    unwatchRoute = watch(
      () => route.path,
      () => {
        checkCurrentRouteAccess()
      }
    )

    // Re-check access after impersonation start/stop clears the transition flag.
    // Permission updates during the transition are intentionally skipped above.
    unwatchImpersonationTransition = watch(
      () => authStore.impersonationTransitionInProgress,
      (inProgress, wasInProgress) => {
        if (wasInProgress && !inProgress) {
          checkCurrentRouteAccess()
        }
      }
    )

    // Only refresh permissions when tab becomes visible after being hidden for 5+ minutes
    // This handles edge cases where permissions are revoked while user is away
    // Permissions are already refreshed on login and page load via router guard
    if (authStore.isAuthenticated) {
      const handleVisibilityChange = async () => {
        if (document.visibilityState === 'hidden') {
          // Track when tab was hidden
          hiddenTimestamp = Date.now()
        } else if (document.visibilityState === 'visible' && authStore.isAuthenticated) {
          // Only refresh if tab was hidden for 5+ minutes (300000ms)
          // This prevents unnecessary refreshes on quick tab switches
          const hiddenDuration = hiddenTimestamp ? Date.now() - hiddenTimestamp : 0
          const fiveMinutes = 5 * 60 * 1000
          
          if (hiddenDuration >= fiveMinutes) {
            try {
              // Refresh user data to get latest permissions after long absence
              await authStore.refreshUser()
              // Check access after refresh
              checkCurrentRouteAccess()
            } catch (error: any) {
              // Don't log timeout errors - they're not critical
              const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout')
              if (!isTimeout) {
                console.error('Failed to refresh user permissions:', error)
              }
              // Don't clear auth on timeout - session might still be valid
            }
          }
          // Reset timestamp
          hiddenTimestamp = null
        }
      }

      // Check permissions when page visibility changes
      visibilityChangeHandler = handleVisibilityChange
      document.addEventListener('visibilitychange', handleVisibilityChange)

      // Also refresh permissions when user interacts with the page (clicks, keyboard)
      // This ensures permission changes are detected immediately when user tries to use the page
      const handleUserInteraction = async () => {
        if (document.visibilityState === 'visible' && authStore.isAuthenticated) {
          try {
            await authStore.refreshUser()
            checkCurrentRouteAccess()
          } catch (error: any) {
            // Don't log timeout errors - they're not critical
            const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout')
            if (!isTimeout) {
              console.error('Failed to refresh user permissions:', error)
            }
          }
        }
      }

      // Throttle interaction handler to avoid too many requests
      let interactionTimeout: number | null = null
      const throttledInteractionHandler = () => {
        if (interactionTimeout) {
          clearTimeout(interactionTimeout)
        }
        interactionTimeout = window.setTimeout(handleUserInteraction, 5000) // Wait 5s after last interaction (avoid stacking with heavy API work)
      }

      document.addEventListener('click', throttledInteractionHandler, { passive: true })
      document.addEventListener('keydown', throttledInteractionHandler, { passive: true })

      // Periodic fallback: long enough to avoid lining up with the same 30s dashboard-style requests
      // and competing for browser per-host connections. refreshUser() is single-flight in the auth store.
      permissionCheckInterval = window.setInterval(async () => {
        if (document.visibilityState === 'visible' && authStore.isAuthenticated) {
          try {
            await authStore.refreshUser()
            checkCurrentRouteAccess()
          } catch (error: any) {
            // Don't log timeout errors - they're not critical
            const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout')
            if (!isTimeout) {
              console.error('Failed to refresh user permissions:', error)
            }
          }
        }
      }, 120000) // Every 2 minutes when visible
    }

    // Initial check
    checkCurrentRouteAccess()
  })

  onUnmounted(() => {
    if (unwatchUser) {
      unwatchUser()
    }
    if (unwatchRoute) {
      unwatchRoute()
    }
    if (unwatchImpersonationTransition) {
      unwatchImpersonationTransition()
    }
    if (visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', visibilityChangeHandler)
    }
    if (permissionCheckInterval) {
      clearInterval(permissionCheckInterval)
    }
  })

  return {
    checkCurrentRouteAccess
  }
}

